/**
 * MailShortcutButton — bouton flottant raccourci messagerie avec lecteur
 * inline (lecture des emails sans naviguer vers /messagerie).
 *
 * 06/02/2026 — création (peek 5 derniers messages).
 * 07/02/2026 — P1 retour user : ajout d'un mode "reader" qui s'ouvre quand
 * on clique un message. On peut le lire en intégralité dans le popover,
 * le marquer lu/non-lu, puis revenir à la liste OU ouvrir la messagerie
 * complète pour répondre à des messages décisionnels.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, X, ChevronRight, ArrowLeft, ExternalLink, MailOpen } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

const TONE_DOT = {
  positive: 'bg-emerald-400',
  encouragement: 'bg-emerald-400',
  warning: 'bg-rose-400',
  advice: 'bg-sky-400',
  neutral: 'bg-slate-400',
  info: 'bg-slate-400',
  urgent: 'bg-rose-500',
  formal: 'bg-slate-400',
  friendly: 'bg-emerald-400',
  aggressive: 'bg-rose-500',
};

const TONE_BORDER = {
  positive: 'border-emerald-500/40',
  encouragement: 'border-emerald-500/40',
  warning: 'border-rose-500/40',
  advice: 'border-sky-500/40',
  urgent: 'border-rose-500/60',
  aggressive: 'border-rose-500/60',
  friendly: 'border-emerald-500/40',
  formal: 'border-slate-500/40',
  neutral: 'border-slate-500/40',
  info: 'border-slate-500/40',
};

function timeAgo(day, totalDays) {
  if (!Number.isFinite(day)) return '';
  const diff = (totalDays || 0) - day;
  if (diff <= 0) return "aujourd'hui";
  if (diff === 1) return 'hier';
  return `il y a ${diff} j`;
}

export default function MailShortcutButton() {
  const navigate = useNavigate();
  const inbox = useGameStore(s => s.inbox || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const gameStarted = useGameStore(s => s.gameStarted);
  const markMessageRead = useGameStore(s => s.markMessageRead);
  const [open, setOpen] = useState(false);
  // Lecteur inline : id du message en cours de lecture, ou null pour la liste.
  const [readingId, setReadingId] = useState(null);
  const popoverRef = useRef(null);

  const unreadCount = (inbox || []).filter(m => !m.read).length;
  const lastFive = [...(inbox || [])]
    .sort((a, b) => (b.day || 0) - (a.day || 0))
    .slice(0, 5);
  const reading = readingId ? (inbox || []).find(m => m.id === readingId) : null;

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
        setReadingId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!gameStarted) return null;

  const goToInbox = () => {
    setOpen(false);
    setReadingId(null);
    navigate('/messagerie');
  };

  const openMessage = (m) => {
    setReadingId(m.id);
    if (!m.read && markMessageRead) {
      markMessageRead(m.id);
    }
  };

  const backToList = () => setReadingId(null);

  // Détermine si le message a des choix décisionnels (nécessite la
  // messagerie complète pour répondre).
  const hasChoices = reading && Array.isArray(reading.choices) && reading.choices.length > 0;
  const alreadyReplied = reading && !!reading.resolvedChoiceId;

  return (
    <div className="fixed top-20 right-4 z-40" ref={popoverRef}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setReadingId(null); }}
        className="relative w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
        aria-label="Raccourci messagerie"
        data-testid="mail-shortcut-btn"
      >
        <Inbox className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none flex items-center justify-center border-2 border-white dark:border-ink-900 shadow"
            data-testid="mail-shortcut-unread-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <span className="absolute right-full mr-3 px-2 py-1 rounded bg-ink-800 text-xs text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Messagerie ({unreadCount} non lu{unreadCount > 1 ? 's' : ''})
        </span>
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 right-0 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-ink-600 bg-ink-900 dark:bg-ink-800 shadow-2xl overflow-hidden"
          data-testid="mail-shortcut-popover"
        >
          {/* Header — switch entre "liste" et "lecteur" */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-ink-600 bg-ink-800 dark:bg-ink-900">
            {reading ? (
              <button
                type="button"
                onClick={backToList}
                className="text-xs text-slate-200 hover:text-white inline-flex items-center gap-1.5"
                data-testid="mail-shortcut-back"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Retour
              </button>
            ) : (
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-200">
                5 derniers messages
              </h4>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); setReadingId(null); }}
              className="text-slate-400 hover:text-slate-200"
              aria-label="Fermer"
              data-testid="mail-shortcut-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* CONTENU */}
          {!reading ? (
            // ── Mode LISTE ──
            <>
              <ul className="max-h-80 overflow-y-auto divide-y divide-ink-700">
                {lastFive.length === 0 ? (
                  <li className="p-4 text-sm text-slate-400 italic text-center">
                    Aucun message pour l&apos;instant.
                  </li>
                ) : lastFive.map((m, i) => (
                  <li
                    key={m.id || m.instanceId || i}
                    className={`p-3 cursor-pointer hover:bg-ink-700/40 transition-colors ${!m.read ? 'bg-ink-700/20' : ''}`}
                    onClick={() => openMessage(m)}
                    data-testid={`mail-shortcut-item-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${TONE_DOT[m.tone] || TONE_DOT.neutral}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className={`text-xs font-semibold truncate ${!m.read ? 'text-white' : 'text-slate-300'}`}>
                            {m.subject || m.title || '(sans objet)'}
                          </p>
                          <span className="text-[10px] text-slate-500 flex-shrink-0">{timeAgo(m.day, totalDays)}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 truncate">
                          {m.senderName || m.sender || 'Service interne'}
                        </p>
                        {/* Aperçu première ligne du corps quand disponible */}
                        {(m.body || m.preview) && (
                          <p className="text-[10px] text-slate-500 truncate mt-0.5">
                            {(m.body || m.preview).split('\n')[0]}
                          </p>
                        )}
                      </div>
                      {Array.isArray(m.choices) && m.choices.length > 0 && !m.resolvedChoiceId && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 flex-shrink-0">
                          Action requise
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={goToInbox}
                className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold flex items-center justify-center gap-1 transition-colors"
                data-testid="mail-shortcut-goto-inbox"
              >
                Voir toute la messagerie <ChevronRight className="w-3 h-3" />
              </button>
            </>
          ) : (
            // ── Mode LECTEUR ──
            <div
              className={`flex flex-col max-h-[28rem] border-l-2 ${TONE_BORDER[reading.tone] || TONE_BORDER.neutral}`}
              data-testid="mail-shortcut-reader"
            >
              <div className="px-4 py-3 border-b border-ink-700 bg-ink-800/60">
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${TONE_DOT[reading.tone] || TONE_DOT.neutral}`} />
                  <div className="flex-1 min-w-0">
                    <h5
                      className="text-sm font-bold text-white leading-tight"
                      data-testid="mail-shortcut-reader-subject"
                    >
                      {reading.subject || reading.title || '(sans objet)'}
                    </h5>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p className="text-[11px] text-slate-300 truncate">
                        <span className="font-semibold">{reading.senderName || reading.sender || 'Service interne'}</span>
                        {reading.senderType && (
                          <span className="text-slate-500"> · {reading.senderType}</span>
                        )}
                      </p>
                      <span className="text-[10px] text-slate-500 flex-shrink-0">{timeAgo(reading.day, totalDays)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 overflow-y-auto flex-1 text-[12px] text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="mail-shortcut-reader-body">
                {reading.body || reading.message || 'Ce message n\'a pas de contenu détaillé.'}
              </div>
              <div className="px-4 py-2 border-t border-ink-700 bg-ink-800/60 flex flex-col gap-2">
                {hasChoices && !alreadyReplied && (
                  <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 inline-flex items-center gap-1.5">
                    <span className="font-bold uppercase tracking-wider">Action requise</span>
                    <span className="text-amber-200">— ouvre la messagerie pour répondre.</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={backToList}
                    className="text-[11px] text-slate-300 hover:text-white inline-flex items-center gap-1"
                    data-testid="mail-shortcut-reader-back"
                  >
                    <ArrowLeft className="w-3 h-3" /> Retour à la liste
                  </button>
                  <button
                    type="button"
                    onClick={goToInbox}
                    className="text-[11px] px-2.5 py-1 rounded bg-sky-600 hover:bg-sky-500 text-white font-bold inline-flex items-center gap-1 transition-colors"
                    data-testid="mail-shortcut-reader-open-inbox"
                  >
                    {hasChoices && !alreadyReplied
                      ? <>Répondre <ExternalLink className="w-3 h-3" /></>
                      : <>Ouvrir <MailOpen className="w-3 h-3" /></>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
